/**
 * ChatMessage - World-Class Minimalist Design
 * 
 * Design philosophy inspired by Apple/Airbnb:
 * - Monochromatic palette (blacks, grays, whites)
 * - Generous whitespace
 * - Typography-first, content is king
 * - Subtle micro-interactions
 * - No unnecessary colors or decorations
 */
import React, { useState, useMemo, useCallback } from 'react';
import { 
  Sparkles, Copy, Check, Bookmark, BookmarkCheck, ChevronRight, 
  FileText, RotateCcw, ThumbsUp, ThumbsDown, FolderPlus, Loader2,
  ExternalLink
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { auth } from '../../utils/auth';
import SaveDeliverableModal from './SaveDeliverableModal';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Process citations - Convert [1], [2], etc. to clickable badges
 */
const processCitations = (
  children: React.ReactNode,
  onCitationClick?: (citationNumber: number) => void
): React.ReactNode => {
  if (!children) return children;
  
  // If children is a string, process it
  if (typeof children === 'string') {
    // Match [1], [2], etc. patterns - superscript citations
    const citationRegex = /\[(\d+)\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    
    while ((match = citationRegex.exec(children)) !== null) {
      // Add text before citation
      if (match.index > lastIndex) {
        parts.push(children.slice(lastIndex, match.index));
      }
      
      // Add clickable citation badge
      const citationNum = parseInt(match[1], 10);
      parts.push(
        <button
          key={`cite-${key++}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('🔗 Citation clicked:', citationNum);
            if (onCitationClick) onCitationClick(citationNum);
          }}
          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold text-violet-600 bg-violet-100 rounded hover:bg-violet-200 hover:text-violet-700 transition-colors cursor-pointer align-super ml-0.5 -mt-1"
          title={`Se kilde ${citationNum}`}
        >
          {citationNum}
        </button>
      );
      
      lastIndex = citationRegex.lastIndex;
    }
    
    // Add remaining text
    if (lastIndex < children.length) {
      parts.push(children.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : children;
  }
  
  // If children is an array, process each element
  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === 'string') {
        return <React.Fragment key={index}>{processCitations(child, onCitationClick)}</React.Fragment>;
      }
      return child;
    });
  }
  
  // Return unchanged for other types (React elements)
  return children;
};

/**
 * Markdown Typography - World-class, readable, elegant
 */
const createMarkdownComponents = (
  onCopyCode: (code: string) => void,
  onCitationClick?: (citationNumber: number) => void
) => ({
  p: ({ children }: any) => (
    <p className="text-[15px] leading-[1.8] text-gray-700 mb-4 last:mb-0">
      {processCitations(children, onCitationClick)}
    </p>
  ),
  h1: ({ children }: any) => (
    <h1 className="text-xl font-semibold text-gray-900 mt-8 mb-4 first:mt-0">{children}</h1>
  ),
  h2: ({ children }: any) => (
    <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-3 first:mt-0">{children}</h2>
  ),
  h3: ({ children }: any) => (
    <h3 className="text-base font-semibold text-gray-900 mt-5 mb-2 first:mt-0">{children}</h3>
  ),
  h4: ({ children }: any) => (
    <h4 className="text-sm font-semibold text-gray-800 mt-4 mb-2 first:mt-0">{children}</h4>
  ),
  ul: ({ children }: any) => (
    <ul className="my-4 space-y-2.5">{children}</ul>
  ),
  ol: ({ children }: any) => (
    <ol className="my-4 space-y-2.5 list-none">{children}</ol>
  ),
  li: ({ children, node, index, ...props }: any) => {
    const isOrdered = node?.parentNode?.tagName === 'ol';
    return (
      <li className="flex items-start gap-3 text-[15px] leading-[1.8] text-gray-700" {...props}>
        {isOrdered ? (
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-medium flex items-center justify-center mt-0.5">
            {(index ?? 0) + 1}
          </span>
        ) : (
          <span className="flex-shrink-0 w-1.5 h-1.5 bg-gray-300 rounded-full mt-[11px]" />
        )}
        <span className="flex-1">{children}</span>
      </li>
    );
  },
  strong: ({ children }: any) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  em: ({ children }: any) => (
    <em className="italic text-gray-600">{children}</em>
  ),
  blockquote: ({ children }: any) => (
    <blockquote className="my-5 pl-4 border-l-2 border-gray-200 text-gray-600 italic">
      {children}
    </blockquote>
  ),
  code({ node, inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';
    
    if (!inline && (language || String(children).includes('\n'))) {
      return (
        <div className="my-5 rounded-xl overflow-hidden bg-gray-900 ring-1 ring-gray-800">
          <div className="flex items-center justify-between px-4 py-2.5 bg-gray-800/80 border-b border-gray-700/50">
            <span className="text-xs text-gray-400 font-medium tracking-wide">{language || 'code'}</span>
            <button
              onClick={() => onCopyCode(String(children))}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
              <span>Copy</span>
            </button>
          </div>
          <SyntaxHighlighter
            style={oneDark}
            language={language || 'text'}
            PreTag="div"
            customStyle={{
              margin: 0,
              padding: '1rem 1.25rem',
              background: 'transparent',
              fontSize: '13px',
              lineHeight: '1.6'
            }}
            {...props}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        </div>
      );
    }
    return (
      <code className="px-1.5 py-0.5 bg-gray-100 text-gray-800 text-[13px] font-mono rounded-md" {...props}>
        {children}
      </code>
    );
  },
  table: ({ children }: any) => (
    <div className="my-5 overflow-x-auto rounded-xl border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">{children}</table>
    </div>
  ),
  thead: ({ children }: any) => <thead className="bg-gray-50">{children}</thead>,
  tbody: ({ children }: any) => <tbody className="divide-y divide-gray-100 bg-white">{children}</tbody>,
  tr: ({ children }: any) => <tr className="hover:bg-gray-50/50 transition-colors">{children}</tr>,
  th: ({ children }: any) => (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }: any) => (
    <td className="px-4 py-3 text-sm text-gray-700">{children}</td>
  ),
  a: ({ href, children }: any) => (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-500 transition-colors inline-flex items-center gap-1"
    >
      {children}
      <ExternalLink className="w-3 h-3 opacity-40" />
    </a>
  ),
  hr: () => <hr className="my-8 border-gray-100" />
});

interface ChatMessageProps {
  message: any;
  caseId: any;
  caseName?: any;
  onSaveAsDeliverable?: any;
  onOpenDraft?: any;
  onDocumentAdded?: any;
  onOpenDocument?: (documentId: string, chunkId?: string) => void;
  isStreaming?: boolean;
  onRegenerate?: any;
  draftInfo?: any;
  isLast?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ 
  message, 
  caseId,
  caseName,
  onSaveAsDeliverable,
  onOpenDraft,
  onDocumentAdded,
  onOpenDocument,
  isStreaming = false,
  onRegenerate,
  draftInfo = null,
  isLast
}) => {
  const { t, i18n } = useTranslation(['common', 'workspace']);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(!!message.deliverable_id);
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const [showDeliverableModal, setShowDeliverableModal] = useState(false);
  const [addingToCase, setAddingToCase] = useState(false);
  const [addedToCase, setAddedToCase] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Handle citation click - opens document viewer
  const handleCitationClick = useCallback((citationNumber: number) => {
    console.log('📚 Citation clicked:', citationNumber, 'sources:', message.sources);
    
    // Check if we have sources array with this citation
    const sources = message.sources || [];
    const source = sources[citationNumber - 1]; // Citations are 1-indexed
    
    if (source && onOpenDocument) {
      onOpenDocument(source.document_id || source.documentId, source.chunk_id || source.chunkId);
    } else {
      // Fallback: Show toast with info
      toast.info(`Kilde ${citationNumber} - Åpne dokumentpanelet for å se kilder`);
    }
  }, [message.sources, onOpenDocument]);

  const markdownComponents = useMemo(() => createMarkdownComponents(
    (code) => {
      navigator.clipboard.writeText(code);
      toast.success('Copied to clipboard');
    },
    handleCitationClick
  ), [handleCitationClick]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleSaveAsDeliverable = () => {
    if (saved) return;
    setShowDeliverableModal(true);
  };
  
  const handleDeliverableSaved = (data: any) => {
    setSaved(true);
    if (onSaveAsDeliverable) onSaveAsDeliverable(data);
  };

  const handleFeedback = (type: 'up' | 'down') => {
    setFeedback(type);
  };

  const handleAddToCase = async (e: React.MouseEvent) => {
    e?.stopPropagation();
    if (!caseId || addingToCase || addedToCase) return;
    
    setAddingToCase(true);
    
    try {
      let documentContent = message.content;
      let documentTitle = draftInfo?.title || 'AI Document';
      
      if (message.draft_id) {
        try {
          const draftResponse = await axios.get(`${API}/drafts/${message.draft_id}`, {
            headers: { Authorization: `Bearer ${auth.getToken()}` }
          });
          if (draftResponse.data?.content) {
            documentContent = draftResponse.data.content;
            documentTitle = draftResponse.data.title || documentTitle;
          }
        } catch {
          console.log('Using message content as fallback');
        }
      }
      
      const safeTitle = documentTitle
        .replace(/[^a-zA-Z0-9æøåÆØÅ\s-]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 50);
      const filename = `${safeTitle}_${new Date().toISOString().split('T')[0]}.docx`;
      
      const response = await axios.post(`${API}/documents/from-ai`, {
        case_id: caseId,
        filename,
        content: documentContent,
        title: documentTitle,
        source: 'ai_workspace',
        thread_id: message.thread_id,
        message_id: message.id
      }, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      
      if (response.data?.success) {
        setAddedToCase(true);
        toast.success('Document added to case');
        if (onDocumentAdded) onDocumentAdded(response.data.document);
      }
    } catch {
      toast.error('Could not add document');
    } finally {
      setAddingToCase(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // USER MESSAGE - Dark, elegant bubble
  // ═══════════════════════════════════════════════════════════
  if (message.role === 'user') {
    return (
      <div className="flex justify-end mb-6">
        <div className="max-w-[75%]">
          {/* Attachments */}
          {message.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2 justify-end">
              {message.attachments.map((a: any, i: number) => (
                <div 
                  key={i} 
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 rounded-lg text-xs text-gray-600"
                >
                  <FileText className="w-3.5 h-3.5 text-gray-400" />
                  <span className="max-w-[140px] truncate font-medium">{a.filename || a}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Message bubble - Dark and elegant */}
          <div className="bg-gray-900 text-white px-4 py-3 rounded-2xl rounded-br-md">
            <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // AI MESSAGE - Clean, minimal, world-class
  // ═══════════════════════════════════════════════════════════
  const hasDraft = message.draft_id || draftInfo;

  return (
    <div 
      className="mb-8 group/message"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header - Avatar + Name */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-xl bg-gray-900 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm text-gray-900">Lexy</span>
          {isStreaming && (
            <span className="flex items-center gap-1.5 text-xs text-gray-400">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gray-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-gray-500"></span>
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Message Content */}
      <div className="pl-11">
        {/* Markdown content */}
        <div className="prose-container">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {message.content}
          </ReactMarkdown>
        </div>
        
        {/* Streaming cursor */}
        {isStreaming && (
          <span className="inline-block w-0.5 h-5 bg-gray-400 ml-0.5 animate-pulse rounded-full" />
        )}

        {/* Document Draft Card - World-class minimal design */}
        {hasDraft && !isStreaming && (
          <div 
            onClick={() => onOpenDraft && onOpenDraft(message.draft_id || draftInfo?.id)}
            className="mt-6 group/card cursor-pointer"
          >
            <div className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-2xl hover:bg-gray-100 hover:border-gray-300 transition-all duration-200">
              {/* Document icon - Monochrome */}
              <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900 truncate">
                  {draftInfo?.title || (i18n.language === 'en' ? 'Document Draft' : 'Dokumentutkast')}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Click to open and edit
                </p>
              </div>

              {/* Actions - Clean, subtle */}
              <div className="flex items-center gap-2">
                {caseId && (
                  <button
                    onClick={handleAddToCase}
                    disabled={addingToCase || addedToCase}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-all",
                      addedToCase 
                        ? "bg-gray-100 text-gray-400" 
                        : "text-gray-600 hover:text-gray-900 hover:bg-white border border-transparent hover:border-gray-200"
                    )}
                  >
                    {addingToCase ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : addedToCase ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <FolderPlus className="w-3.5 h-3.5" />
                    )}
                    <span className="hidden sm:inline">{addedToCase ? 'Added' : 'Add to case'}</span>
                  </button>
                )}
                <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center group-hover/card:bg-gray-900 group-hover/card:border-gray-900 transition-all">
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover/card:text-white transition-colors" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action toolbar - Appears on hover, very subtle */}
        {!isStreaming && message.content && (
          <div 
            className={cn(
              "flex items-center gap-0.5 mt-4 transition-all duration-200",
              isHovered ? "opacity-100" : "opacity-0"
            )}
          >
            {/* Copy */}
            <ActionButton
              onClick={handleCopy}
              icon={copied ? Check : Copy}
              active={copied}
            />
            
            {/* Regenerate */}
            {onRegenerate && (
              <ActionButton
                onClick={onRegenerate}
                icon={RotateCcw}
              />
            )}
            
            {/* Feedback */}
            <ActionButton
              onClick={() => handleFeedback('up')}
              icon={ThumbsUp}
              active={feedback === 'up'}
            />
            <ActionButton
              onClick={() => handleFeedback('down')}
              icon={ThumbsDown}
              active={feedback === 'down'}
            />

            {/* Spacer */}
            <div className="flex-1" />
            
            {/* Save */}
            <button
              onClick={handleSaveAsDeliverable}
              disabled={saved}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all",
                saved 
                  ? "text-gray-300 cursor-default" 
                  : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
              )}
            >
              {saved ? (
                <BookmarkCheck className="w-3.5 h-3.5" />
              ) : (
                <Bookmark className="w-3.5 h-3.5" />
              )}
              <span>{saved ? 'Saved' : 'Save'}</span>
            </button>
          </div>
        )}
      </div>
      
      {/* Save Modal */}
      <SaveDeliverableModal
        isOpen={showDeliverableModal}
        onClose={() => setShowDeliverableModal(false)}
        messageId={message.id}
        content={message.content}
        caseId={caseId}
        caseName={caseName}
        onSaved={handleDeliverableSaved}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Action Button - Minimal, clean
// ═══════════════════════════════════════════════════════════
interface ActionButtonProps {
  onClick: () => void;
  icon: React.FC<{ className?: string }>;
  active?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({ 
  onClick, 
  icon: Icon, 
  active = false
}) => (
  <button 
    onClick={onClick}
    className={cn(
      "p-2 rounded-lg transition-all",
      active 
        ? "text-gray-600 bg-gray-100" 
        : "text-gray-300 hover:text-gray-500 hover:bg-gray-50"
    )}
  >
    <Icon className="w-4 h-4" />
  </button>
);

export default ChatMessage;
